import moment from "moment";
import { nanoid } from "nanoid";

import * as date from "@/utils/date";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";
import * as pdfApi from "@/apiv2/pdfCustomisation";
import * as alertActions from "@/redux/alerts/alerts.actions";

import { dependencies } from "../../../../package.json";

import * as selectors from "./template-lists.selectors";
import {
  RESET,
  SET_IS_FETCHING_TEMPLATES_STATUS,
  SET_TEMPLATES,
  SET_LOADING_STATE,
  SET_LOADING_STATE_BY_UID,
  SET_LOADING_STATE_ON_NEW_UID,
  SET_IS_SELECT_TEMPLATE_MODAL_VISIBLE,
} from "./template-lists.actionTypes";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsFetchingTemplatesStatus = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_TEMPLATES_STATUS,
    payload: status,
  });
};

export const setTemplates = (data) => (dispatch) => {
  dispatch({
    type: SET_TEMPLATES,
    payload: data,
  });
};

export const setIsSelectTemplateModalVisible = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_SELECT_TEMPLATE_MODAL_VISIBLE,
    payload: data,
  });
};

export const setLoadingState = (data) => (dispatch) => {
  dispatch({
    type: SET_LOADING_STATE,
    payload: data,
  });
};

export const setLoadingStateByUid =
  ({ uid, key, value }) =>
  (dispatch) => {
    dispatch({
      type: SET_LOADING_STATE_BY_UID,
      payload: {
        uid,
        key,
        value,
      },
    });
  };

export const setLoadingStateOnNewUid =
  ({ uid }) =>
  (dispatch) => {
    dispatch({
      type: SET_LOADING_STATE_ON_NEW_UID,
      payload: { uid },
    });
  };

export const fetchTemplates = () => async (dispatch) => {
  try {
    dispatch(setIsFetchingTemplatesStatus(STATUS.PENDING));

    const { data: globalTemplates } = await pdfApi.getGlobalTemplates()();
    const { data: companyTemplates } = await pdfApi.getCompanyTemplates()();

    dispatch(
      setTemplates([
        ...globalTemplates
          .filter((t) => t.key === "invoice" || t.key === "partial_invoice")
          .map((t) => ({
            ...t,
            canEdit: false,
            templateKey: t.key,
            isGlobal: true,
            comingSoon: false,
            isDefault:
              companyTemplates.filter(
                (company) => company.templateKey === t.key && company.isDefault
              ).length === 0,
          })),
        ...globalTemplates
          .filter((t) => t.key !== "invoice" && t.key !== "partial_invoice")
          .map((t) => ({
            ...t,
            canEdit: false,
            templateKey: t.key,
            isGlobal: true,
            comingSoon: true,
            isDefault:
              companyTemplates.filter(
                (company) => company.templateKey === t.key && company.isDefault
              ).length === 0,
          })),
        ...companyTemplates.map((t) => ({ ...t, canEdit: true, isGlobal: false })),
      ])
    );

    dispatch(
      setLoadingState([...globalTemplates.map((t) => t.uid), ...companyTemplates.map((t) => t.uid)])
    );

    dispatch(setIsFetchingTemplatesStatus(STATUS.FULFILLED));
  } catch (error) {
    dispatch(setIsFetchingTemplatesStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

export const setDefaulTemplate = (templateUid) => async (dispatch, getState) => {
  const state = getState();
  const templates = selectors.getTemplates(state);
  const template = templates.find((t) => t.uid === templateUid);

  if (!template) return;

  const formData = new FormData();

  if (template.isGlobal) {
    const templateByKey = templates.find(
      (t) => t.templateKey === template.templateKey && t.isDefault
    );

    dispatch(
      setTemplates(
        templates.map((t) => {
          if (t.uid === template.uid) {
            return { ...t, isDefault: true };
          } else if (t.uid === templateByKey.uid) {
            return { ...t, isDefault: false };
          } else {
            return { ...t, isDefault: t.isDefault };
          }
        })
      )
    );

    if (templateByKey) {
      formData.append("is_default", false);
      await pdfApi.patchCompanyTemplate({ uid: templateByKey.uid, data: formData })();
    }
  } else {
    const templateByKey = templates.find(
      (t) => t.templateKey === template.templateKey && t.isDefault
    );

    dispatch(
      setTemplates(
        templates.map((t) => {
          if (t.uid === template.uid) {
            return { ...t, isDefault: true };
          } else if (t.uid === templateByKey.uid) {
            return { ...t, isDefault: false };
          } else {
            return { ...t, isDefault: t.isDefault };
          }
        })
      )
    );

    formData.append("is_default", true);
    await pdfApi.patchCompanyTemplate({ uid: template.uid, data: formData })();
  }
};

export const toggleArchive =
  ({ templateUid, isArchive = false }) =>
  async (dispatch, getState) => {
    const state = getState();
    const templates = selectors.getTemplates(state);
    const formData = new FormData();

    dispatch(
      setTemplates(
        templates.map((t) => (t.uid === templateUid ? { ...t, isDeleted: isArchive } : t))
      )
    );

    formData.append("is_deleted", isArchive);
    await pdfApi.patchCompanyTemplate({ uid: templateUid, data: formData })();
  };

export const duplicateGlobalTemplate = (templateUid) => async (dispatch, getState) => {
  const state = getState();
  const templates = selectors.getTemplates(state);

  dispatch(setLoadingStateByUid({ uid: templateUid, key: "duplicating", value: STATUS.PENDING }));

  try {
    const { data } = await pdfApi.getGlobalTemplate(templateUid)();

    const formData = new FormData();
    formData.append("global_template", data.uid);
    formData.append("content", JSON.stringify(data.content));
    formData.append("is_default", false);
    formData.append("name", `Copy of ${data.name}`);
    formData.append("description", "");
    formData.append("version", dependencies["@factory/pdf-customisation"]);

    const blob = new Blob([data.html_content], { type: "text/html" });
    const htmlFile = new File(
      [blob],
      `${data.key}-${date.toLocalDateTime({
        date: moment.tz(date.getCurrentUserTimezone()),
        timezone: date.getCurrentUserTimezone(),
        format: "YYYY-MM-DDTHHmmssZ",
      })}.html`,
      { type: "text/html" }
    );
    formData.append("html_file", htmlFile);

    const { data: newTemplate } = await pdfApi.postCompanyTemplate(formData)();

    dispatch(setLoadingStateOnNewUid({ uid: newTemplate.uid }));

    dispatch(setTemplates([...templates, { ...newTemplate, canEdit: true, isGlobal: false }]));

    dispatch(
      setLoadingStateByUid({ uid: templateUid, key: "duplicating", value: STATUS.FULFILLED })
    );
  } catch (error) {
    dispatch(
      setLoadingStateByUid({ uid: templateUid, key: "duplicating", value: STATUS.REJECTED })
    );

    handleError(error, dispatch);
  }
};

export const duplicateCompanyTemplate = (templateUid) => async (dispatch, getState) => {
  const state = getState();
  const templates = selectors.getTemplates(state);

  dispatch(setLoadingStateByUid({ uid: templateUid, key: "duplicating", value: STATUS.PENDING }));

  try {
    const { data } = await pdfApi.getCompanyTemplate(templateUid)();

    const invoiceGlobalTemplate = templates.find((t) => t.templateKey === data.template_key);
    if (!invoiceGlobalTemplate) {
      dispatch(
        alertActions.createAlertAction(nanoid(), "Unable to duplicate template", true, "danger")
      );
    }

    const formData = new FormData();
    formData.append("global_template", invoiceGlobalTemplate?.uid ?? null);
    formData.append("content", JSON.stringify(data.content));
    formData.append("is_default", false);
    formData.append("name", `Copy of ${data.name}`);
    formData.append("description", "");
    formData.append("version", dependencies["@factory/pdf-customisation"]);

    const blob = new Blob([data.html_content], { type: "text/html" });
    const htmlFile = new File(
      [blob],
      `${data.key}-${date.toLocalDateTime({
        date: moment.tz(date.getCurrentUserTimezone()),
        timezone: date.getCurrentUserTimezone(),
        format: "YYYY-MM-DDTHHmmssZ",
      })}.html`,
      { type: "text/html" }
    );
    formData.append("html_file", htmlFile);

    const { data: newTemplate } = await pdfApi.postCompanyTemplate(formData)();

    dispatch(setLoadingStateOnNewUid({ uid: newTemplate.uid }));

    dispatch(setTemplates([...templates, { ...newTemplate, canEdit: true, isGlobal: false }]));

    dispatch(
      setLoadingStateByUid({ uid: templateUid, key: "duplicating", value: STATUS.FULFILLED })
    );
  } catch (error) {
    dispatch(
      setLoadingStateByUid({ uid: templateUid, key: "duplicating", value: STATUS.REJECTED })
    );

    handleError(error, dispatch);
  }
};
